import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'

import type { ApiError } from './api-error'
import type { CrudService } from './crud-service'
import { createOptimisticCache } from './optimistic-cache'
import type { PageParams, Paged } from './pagination'
import { createQueryKeys } from './query-keys'

type ReadOptions<TData> = Omit<UseQueryOptions<TData, ApiError>, 'queryKey' | 'queryFn'>

/**
 * Everything TanStack Query needs for one resource, built from its service so a module's `*.queries.ts` stays a few lines:
 *
 *   keys / queries     the cache keys, and `queryOptions` for use anywhere (a hook, a route loader, `queryClient.fetchQuery`)
 *   useList            one page; the previous page stays on screen while the next loads (no flicker when paging)
 *   useAll             every page as one array
 *   useOne             a single item (waits while `id` is undefined)
 *   useCreate          creates, then refreshes the lists
 *   useUpdate          updates with an OPTIMISTIC change: the screen changes at once and is put back if the server refuses
 *   useRemove          removes with an optimistic removal, the same way
 *
 * Reads are retried only when retrying can help, and shared between components that ask for the same thing (one request, even
 * when a page is swapped for another that needs the same data). Failed mutations show an error toast (see `query-client.ts`).
 */
export function createResource<TDto extends { id: string }, TCreate, TUpdate, TFilters extends object, TCreated = TDto>(
  name: string,
  service: CrudService<TDto, TCreate, TUpdate, TFilters, TCreated>,
) {
  const keys = createQueryKeys(name)
  const optimistic = createOptimisticCache<TDto>(keys)

  // The query's `signal` is deliberately NOT passed on. Passing it makes TanStack Query cancel a request the moment its last
  // component unmounts, and when one page is swapped for another that needs the same data (a route change) that means a request
  // is cancelled and sent again. Letting it finish and sharing the answer is both faster and quieter. A service still accepts a
  // signal for a caller that really wants cancellation.
  const queries = {
    list: (filters?: TFilters & PageParams) => queryOptions({ queryKey: keys.list(filters), queryFn: () => service.list(filters) }),
    all: (filters?: TFilters) => queryOptions({ queryKey: keys.all(filters), queryFn: () => service.listAll(filters) }),
    detail: (id: string) => queryOptions({ queryKey: keys.detail(id), queryFn: () => service.get(id) }),
  }

  function useList(filters?: TFilters & PageParams, options?: ReadOptions<Paged<TDto>>) {
    return useQuery({ ...queries.list(filters), placeholderData: keepPreviousData, ...options } as UseQueryOptions<Paged<TDto>, ApiError>)
  }

  function useAll(filters?: TFilters, options?: ReadOptions<TDto[]>) {
    return useQuery({ ...queries.all(filters), ...options } as UseQueryOptions<TDto[], ApiError>)
  }

  function useOne(id: string | undefined, options?: ReadOptions<TDto>) {
    return useQuery({ ...queries.detail(id ?? ''), ...options, enabled: Boolean(id) && (options?.enabled ?? true) } as UseQueryOptions<TDto, ApiError>)
  }

  function useCreate() {
    const queryClient = useQueryClient()
    return useMutation<TCreated, ApiError, TCreate>({
      mutationFn: (body) => service.create(body),
      onSuccess: () => Promise.all([queryClient.invalidateQueries({ queryKey: keys.lists() }), queryClient.invalidateQueries({ queryKey: keys.alls() })]),
    })
  }

  function useUpdate() {
    const queryClient = useQueryClient()
    return useMutation<TDto, ApiError, { id: string; body: TUpdate }, { snapshot: Parameters<typeof optimistic.restore>[1] }>({
      mutationFn: ({ id, body }) => service.update(id, body),
      // The body's fields are the item's fields (name, roles, status…), so they can be laid over the cached item as they are.
      onMutate: async ({ id, body }) => {
        const snapshot = await optimistic.begin(queryClient)
        optimistic.patch(queryClient, id, (item) => ({ ...item, ...(body as object) }))
        return { snapshot }
      },
      onError: (_error, _vars, context) => optimistic.restore(queryClient, context?.snapshot),
      // Whatever happened, the server's version is the truth.
      onSettled: () => queryClient.invalidateQueries({ queryKey: keys.root }),
    })
  }

  function useRemove() {
    const queryClient = useQueryClient()
    return useMutation<void, ApiError, string, { snapshot: Parameters<typeof optimistic.restore>[1] }>({
      mutationFn: (id) => service.remove(id),
      onMutate: async (id) => {
        const snapshot = await optimistic.begin(queryClient)
        optimistic.drop(queryClient, id)
        return { snapshot }
      },
      onError: (_error, _id, context) => optimistic.restore(queryClient, context?.snapshot),
      onSettled: () => queryClient.invalidateQueries({ queryKey: keys.root }),
    })
  }

  return { keys, queries, service, useList, useAll, useOne, useCreate, useUpdate, useRemove }
}
